import * as z from "zod";
import { cursorPageOf, cursorPagination } from "../index";
import { outboxKinds, outboxStatuses } from "./enums";

// The error queue of the integrations (M16, FR-M16-04; spec v0.2 §4, rule 1): the rows of `platform.outbox`, events
// and messages to TIL alike, that did not end well or are not advancing. Internal users only.

/**
 * Why a row is in the queue:
 * - `dead`: Nexxt gave up on it (a subscriber of the event did, TIL refused the message for good, or 24 hours of
 *   retries went by). The only one that can be sent again from here;
 * - `failed`: the system behind TIL rejected the message or failed on it. Sending the same message again would be
 *   answered as a duplicate: the domain sends a new one (FR-M16-03);
 * - `overdue`: TIL accepted the message and its outcome did not come back in time;
 * - `stalled`: not delivered yet, long after it was written.
 */
export const integrationProblems = ["dead", "failed", "overdue", "stalled"] as const;
export type IntegrationProblem = (typeof integrationProblems)[number];

/** The error queue, the latest written first. */
export const listIntegrationErrorsInput = z.object({
  problem: z.enum(integrationProblems).optional(),
  kind: z.enum(outboxKinds).optional(),
  /** `I-xx`, or the type of an event: exact. */
  messageType: z.string().trim().min(1).max(80).optional(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 200 }),
});
export type ListIntegrationErrorsInput = z.input<typeof listIntegrationErrorsInput>;

/** A row of the error queue. The payload stays in the DB: it may hold personal data. */
export const integrationErrorDto = z
  .object({
    /** Id of the outbox row: the `messageId` of a message to TIL. */
    id: z.uuid(),
    kind: z.enum(outboxKinds),
    messageType: z.string(),
    /** The entity the message is about, e.g. `catalog.linesheet`. */
    aggregateType: z.string(),
    aggregateId: z.uuid(),
    /**
     * The entity whose page shows this one, where its domain sends it again (ADR-164): the linesheet of a
     * `catalog.sync_run`, the organization of a `party.contact_person`; null for the others.
     */
    parentId: z.uuid().nullable(),
    problem: z.enum(integrationProblems),
    status: z.enum(outboxStatuses),
    /** Publications of an event, calls to TIL of a message. */
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    /** The workers that gave up on a dead event: a retry reaches them alone. */
    subscribers: z.array(z.string()),
    retryable: z.boolean(),
    createdAt: z.iso.datetime(),
    sentAt: z.iso.datetime().nullable(),
    nextAttemptAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
    /** The request id of what wrote the message: the same in the logs of gateway and domains. */
    correlationId: z.string().nullable(),
  })
  .meta({ id: "IntegrationError" });

export const integrationErrorPageDto = cursorPageOf(integrationErrorDto).meta({ id: "IntegrationErrorPage" });

export const getIntegrationErrorSummaryInput = z.object({});
export type GetIntegrationErrorSummaryInput = z.input<typeof getIntegrationErrorSummaryInput>;

/** How many rows the error queue holds, by problem, and since when the oldest stalled one is waiting. */
export const integrationErrorSummaryDto = z
  .object({
    dead: z.number().int(),
    failed: z.number().int(),
    overdue: z.number().int(),
    stalled: z.number().int(),
    oldestStalledAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "IntegrationErrorSummary" });
export type IntegrationErrorSummary = z.output<typeof integrationErrorSummaryDto>;

/** FR-M16-04: sends `dead` rows again. The others are skipped: only a row Nexxt gave up on can be retried. */
export const retryIntegrationMessagesInput = z.object({
  ids: z.array(z.uuid()).min(1).max(100),
});
export type RetryIntegrationMessagesInput = z.input<typeof retryIntegrationMessagesInput>;

export const integrationRetryDto = z
  .object({
    /** Back in the queue: they leave right away. */
    retried: z.array(z.uuid()),
    /** Not `dead` anymore, or never there. */
    skipped: z.array(z.uuid()),
  })
  .meta({ id: "IntegrationRetry" });
export type IntegrationRetry = z.output<typeof integrationRetryDto>;
